import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Container, useTheme, useMediaQuery, Toolbar } from '@mui/material';
import Header from './Header';
import Footer from './Footer';
import InfoboxSchutz from './InfoboxSchutz';
import './Layout.css';
import { useMatomoTracking } from '../hooks/useMatomoTrackingHook';

const Layout = ({ children, infobox = false, pageTitle, mainContent = 'jumpmark-maincontent' }) => {
  const { pathname } = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const footerRef = useRef(null);
  const [offsetBottom, setOffsetBottom] = useState(0);

  // Seitentitel setzen
  useEffect(() => {
    if (pageTitle) {
      document.title = pageTitle;
    }
  }, [pageTitle]);

  useMatomoTracking();

  // Funktion zur Prüfung, ob der Footer sichtbar ist
  const checkFooterVisibility = useCallback(() => {
    // Footer-Sichtbarkeit initial setzen
    let isFooterVisible = false;
    if (footerRef.current) {
      const footerRect = footerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Footer ist sichtbar, wenn er im Viewport ist
      isFooterVisible = footerRect.top < windowHeight;

      // Wenn Footer sichtbar ist, wird der Überlappungsbereich berechnet
      // (auch falls Footer nur teilweise sichtbar ist)
      if (isFooterVisible) {
        const overlap = windowHeight - footerRect.top + 1;
        setOffsetBottom(overlap);
      } else {
        setOffsetBottom(0);
      }
    }
  }, []);

  // Beim Routing-Wechsel (Masken-Wechsel) Footer-Sichtbarkeit zurücksetzen und neu prüfen
  useEffect(() => {
    // Zurücksetzen des Zustands
    setOffsetBottom(0);

    // Nach kurzer Verzögerung erneut prüfen (um CSS-Transitions/Layoutberechnungen abzuwarten)
    setTimeout(checkFooterVisibility, 50);
  }, [pathname, checkFooterVisibility]);

  // Effect für Scroll- und Resize-Events
  useEffect(() => {
    const checkIfDocumentReady = () => {
      if (document.readyState === 'complete') {
        checkFooterVisibility();
      }
    };

    // Sofort prüfen
    checkIfDocumentReady();

    // Falls noch nicht vollständig geladen, auf load-Event warten
    window.addEventListener('load', checkFooterVisibility);
    window.addEventListener('scroll', checkFooterVisibility);
    window.addEventListener('resize', checkFooterVisibility);

    return () => {
      window.removeEventListener('load', checkFooterVisibility);
      window.removeEventListener('scroll', checkFooterVisibility);
      window.removeEventListener('resize', checkFooterVisibility);
    };
  }, [checkFooterVisibility]); // Leeres Array: der Effect wird nur einmal beim Mounting ausgeführt

  const handleSkipLinkFocus = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleSkipLinkKeyDown = e => {
    // Nur auf die Leertaste reagieren
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault(); // Standard-Verhalten verhindern

      const targetElement = document.getElementById(mainContent);

      if (targetElement) {
        // Fokus setzen
        targetElement.focus({ preventScroll: true });
      }
    }
  };

  return (
    <Box className="layout-root">
      {/* Header über die gesamte Breite */}
      <Header />

      {/* Platzhalter für Header */}
      <Toolbar className="layout-toolbar-spacer" />

      {/* Content Wrapper mit beschränkter Breite */}
      <Box className="layout-content-wrapper">
        {/* Hauptinhalt */}
        <Container component="main" className="layout-main" maxWidth={false} disableGutters>
          <a
            href={`#${mainContent}`}
            className="skip-link"
            onFocus={handleSkipLinkFocus}
            onKeyDown={handleSkipLinkKeyDown}
            onClick={e => e.preventDefault()} // Optional: Klicks verhindern
            aria-label="Leertaste drücken, um zum Hauptinhalt zu springen"
          >
            Zum Hauptinhalt springen
          </a>
          {children}
        </Container>
      </Box>

      {/* Spacer für den Footer, um sicherzustellen, dass der Inhalt nicht verdeckt wird */}
      <Box className="footer-spacer" />

      {/* Footer-Container mit fester Position am unteren Rand */}
      <Box className="footer-container">
        {/* Fixierte InfoboxSchutz für Mobile/Tablet */}
        {!isDesktop && infobox && (
          <Box
            className="mobile-infobox-fixed"
            style={{ width: '100%', maxWidth: '100vw' }}
            sx={{
              transition: 'bottom 0.3s ease',
              bottom: `${offsetBottom}px`,
            }}
          >
            <InfoboxSchutz />
          </Box>
        )}

        {/* Footer über die gesamte Breite mit Ref */}
        <Footer ref={footerRef} />
      </Box>
    </Box>
  );
};

export default Layout;
